/**
 * Zkouška – plán nácviku repertoáru.
 * Body: která písnička / pasáž se má na zkoušce zopakovat nebo doladit.
 * Nevyřešené nahoře, hotové dole. Každý přihlášený může přidat, upravit i odškrtnout.
 */
import { supabase } from '../lib/supabase.js';

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
// Odkaz z tipu bereme jako nedůvěryhodný (upravit tip smí kdokoli)
function safeUrl(url) {
    const u = String(url || '').trim();
    return /^https?:\/\//i.test(u) ? u : null;
}

// Které řádky má uživatel rozkliknuté – přežije překreslení seznamu
const openRows = new Set();

// Odkaz bodu: vlastní má přednost před odkazem navázaného tipu
function itemYoutube(it, tipsMap) {
    const own = safeUrl(it.youtube_url);
    if (own) return own;
    const tip = it.tip_id ? tipsMap.get(it.tip_id) : null;
    return tip ? safeUrl(tip.youtube_url) : null;
}

function migrationHint(error) {
    const code = error?.code || '';
    if (code === '42703' || code === 'PGRST204' || /column .* does not exist/i.test(error?.message || '')) {
        return ' — vypadá to, že v Supabase ještě neproběhla migrace 25-rehearsal-youtube.sql.';
    }
    return '';
}

export async function renderZkouska(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { container.innerHTML = '<div class="state-banner">Nejdřív se přihlas.</div>'; return; }

    const itemsRes = await supabase.from('rehearsal_items')
        .select('*').order('created_at', { ascending: false });
    if (itemsRes.error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(itemsRes.error.message)}</div>`;
        return;
    }

    const items = itemsRes.data || [];

    // Body vzniklé z Nových tipů — do rozkliku doplníme původní tip a jeho YT odkaz
    const tipIds = [...new Set(items.map((i) => i.tip_id).filter(Boolean))];
    const tipsMap = new Map();
    if (tipIds.length) {
        const { data: tipRows } = await supabase.from('song_tips')
            .select('id, title, youtube_url, status').in('id', tipIds);
        (tipRows || []).forEach((t) => tipsMap.set(t.id, t));
    }

    // Aktivní (k nácviku) nahoře, odzkoušené v historii
    const todo = items.filter((i) => !i.done);
    const done = items.filter((i) => i.done);

    container.innerHTML = `
        <div class="filters">
            <h2 style="font-size:18px; margin-right:auto;">Zkouška — plán nácviku</h2>
            <span style="font-size:13px;color:var(--text-muted);margin-right:12px;">
                ${todo.length} k nácviku · ${done.length} v historii
            </span>
            <button class="btn btn-sm btn-primary" id="add-reh-btn">+ Nový bod</button>
        </div>

        <div class="reh-list">
            ${todo.length > 0
                ? todo.map((it) => renderRow(it, user.id, tipsMap)).join('')
                : `<div class="state-banner">${done.length > 0
                    ? 'Vše odzkoušeno ✓ — body najdeš v Historii níže.'
                    : 'Žádné body. Klikni <strong>+ Nový bod</strong> a přidej, co je třeba na zkoušce zopakovat.'}</div>`}
        </div>

        ${done.length > 0 ? `
        <details class="reh-history">
            <summary>Historie — odzkoušené (${done.length})</summary>
            <div class="reh-list">
                ${done.map((it) => renderRow(it, user.id, tipsMap)).join('')}
            </div>
        </details>
        ` : ''}

        <div class="footer-note">
            <strong>Jak to funguje:</strong> přidej píseň nebo pasáž, která potřebuje doladit.
            Po nácviku zaškrtni <em>hotovo</em> — bod se přesune do <strong>Historie</strong> (rozbalovací sekce dole).
            Odškrtnutím se vrátí zpět mezi aktivní body. Tlačítkem <strong>✏</strong> bod upravíš — dopsat nebo opravit údaje může kdokoli. Tlačítkem <strong>▸</strong> rozklikneš celou poznámku a původ bodu. Odkaz <strong>▶ YT</strong> vedle názvu otevře nahrávku na YouTube — doplníš ho přes ✏ u kteréhokoli bodu.
        </div>
    `;

    container.querySelector('#add-reh-btn').addEventListener('click', () => {
        openRehModal({ onSaved: () => renderZkouska(container) });
    });

    container.querySelectorAll('[data-done]').forEach((cb) => {
        cb.addEventListener('change', async () => {
            const { error } = await supabase.from('rehearsal_items')
                .update({ done: cb.checked }).eq('id', cb.dataset.done);
            if (error) { alert('Uložení selhalo: ' + error.message); return; }
            await renderZkouska(container);
        });
    });

    container.querySelectorAll('[data-edit-reh]').forEach((el) => {
        el.addEventListener('dblclick', (e) => {
            e.preventDefault();
            window.getSelection?.()?.removeAllRanges();
            const it = items.find((x) => x.id === el.dataset.editReh);
            if (it) openRehModal({ item: it, onSaved: () => renderZkouska(container) });
        });
    });

    // Rozklik — přepínáme jen třídu, bez překreslení (žádný další dotaz do DB)
    container.querySelectorAll('[data-toggle-reh]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.toggleReh;
            const wrap = container.querySelector(`.reh-item[data-item="${id}"]`);
            if (!wrap) return;
            const nowOpen = !wrap.classList.contains('open');
            wrap.classList.toggle('open', nowOpen);
            btn.setAttribute('aria-expanded', String(nowOpen));
            if (nowOpen) openRows.add(id); else openRows.delete(id);
        });
    });

    container.querySelectorAll('[data-edit-btn-reh]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const it = items.find((x) => x.id === btn.dataset.editBtnReh);
            if (it) openRehModal({ item: it, onSaved: () => renderZkouska(container) });
        });
    });

    container.querySelectorAll('[data-del-reh]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const it = items.find((x) => x.id === btn.dataset.delReh);
            if (!confirm(`Smazat bod „${it?.song_title}"?`)) return;
            const { error } = await supabase.from('rehearsal_items').delete().eq('id', btn.dataset.delReh);
            if (error) { alert('Smazání selhalo: ' + error.message); return; }
            await renderZkouska(container);
        });
    });
}

function renderRow(it, userId, tipsMap) {
    const isMine = it.created_by === userId;
    const isOpen = openRows.has(it.id);
    const yt = itemYoutube(it, tipsMap);
    // Poznámka je v řádku VŽDY jen na jednu linku (ellipsis) — celá je v rozkliku.
    // Díky tomu má řádek pevnou výšku bez ohledu na délku poznámky.
    return `
        <div class="reh-item${isOpen ? ' open' : ''}${it.done ? ' done' : ''}" data-item="${it.id}">
            <div class="reh-row${it.done ? ' done' : ''}">
                <label class="reh-check" title="${it.done ? 'Hotovo' : 'Označit jako hotové'}">
                    <input type="checkbox" data-done="${it.id}" ${it.done ? 'checked' : ''}>
                </label>
                <div class="reh-titlewrap">
                    <div class="reh-title" data-edit-reh="${it.id}" title="Dvojklik = upravit">${esc(it.song_title)}</div>
                    ${yt ? `<a class="reh-yt" href="${esc(yt)}" target="_blank" rel="noopener noreferrer"
                              title="Přehrát na YouTube: ${esc(yt)}" aria-label="Přehrát na YouTube">▶ YT</a>` : ''}
                </div>
                <div class="reh-note">${esc(it.note || '')}</div>
                <div class="reh-actions">
                    <button class="reh-act" data-edit-btn-reh="${it.id}"
                            aria-label="Upravit bod" title="Upravit — dopsat nebo opravit údaje">✏</button>
                    <button class="reh-act reh-toggle" data-toggle-reh="${it.id}"
                            aria-expanded="${isOpen}" aria-label="Podrobnosti"
                            title="Rozkliknout podrobnosti">▸</button>
                    ${/* Smazání je až na kraji — mezi ✏ a ▸ by ho překlep trefoval příliš snadno */''}
                    ${isMine ? `<button class="reh-act reh-del" data-del-reh="${it.id}" title="Smazat">🗑</button>` : ''}
                </div>
            </div>
            ${renderDetail(it, tipsMap, isMine)}
        </div>
    `;
}

function renderDetail(it, tipsMap, isMine) {
    const tip = it.tip_id ? tipsMap.get(it.tip_id) : null;
    const yt = itemYoutube(it, tipsMap);
    const origin = tip
        ? `Z Nových tipů: <strong>${esc(tip.title)}</strong>${
            tip.status === 'wip' ? '' : ' <em>(tip už mezi „na zkoušku" není)</em>'}`
        : '';
    return `
        <div class="reh-detail">
            <div class="reh-detail-note">${it.note
                ? esc(it.note)
                : '<em>Bez poznámky — co přesně doladit, doplň přes Upravit.</em>'}</div>
            ${(origin || it.done) ? `
            <div class="reh-detail-meta">
                ${it.done ? '<strong>Odzkoušeno</strong>' : ''}${(it.done && origin) ? ' · ' : ''}${origin}
            </div>` : ''}
            <div class="reh-detail-actions">
                <button class="btn btn-sm" data-edit-btn-reh="${it.id}">✏ Upravit</button>
                ${yt ? `<a class="btn btn-sm" href="${esc(yt)}" target="_blank" rel="noopener noreferrer">▶ YouTube</a>` : ''}
                ${isMine ? `<button class="btn btn-sm rep-btn-danger" data-del-reh="${it.id}">🗑 Smazat</button>` : ''}
            </div>
        </div>
    `;
}

function openRehModal({ item = null, onSaved } = {}) {
    const isEdit = !!item;
    let m = document.getElementById('reh-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'reh-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window" style="max-width: 480px;">
            <div class="modal-head">
                <h2>${isEdit ? 'Upravit bod' : 'Nový bod nácviku'}</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="reh-form">
                <div class="modal-body">
                    <label class="se-field">
                        <span>Písnička / pasáž</span>
                        <input type="text" name="song_title" required autofocus
                               placeholder="např. Apache — předěl do refrénu"
                               value="${esc(item?.song_title || '')}">
                    </label>
                    <label class="se-field" style="margin-top:12px;">
                        <span>Odkaz na YouTube (nepovinné)</span>
                        <input type="url" name="youtube_url"
                               placeholder="https://youtube.com/..."
                               value="${esc(item?.youtube_url || '')}">
                    </label>
                    <label class="se-field" style="margin-top:12px;">
                        <span>Co doladit / nacvičit</span>
                        <textarea name="note" rows="3"
                                  placeholder="(nepovinné) — co konkrétně zlepšit">${esc(item?.note || '')}</textarea>
                    </label>
                    <p id="reh-error" style="color:var(--accent);font-size:13px;margin-top:10px;display:none;"></p>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="reh-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">${isEdit ? 'Uložit změny' : 'Přidat'}</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    const close = () => m.remove();
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#reh-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });
    document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
    });

    m.querySelector('#reh-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const errEl = m.querySelector('#reh-error');
        errEl.style.display = 'none';
        const fd = new FormData(e.target);
        const songTitle = fd.get('song_title').trim();
        const note = fd.get('note').trim();
        const youtube = (fd.get('youtube_url') || '').trim();
        if (!songTitle) { errEl.textContent = 'Název písničky je povinný.'; errEl.style.display = 'block'; return; }
        if (youtube && !safeUrl(youtube)) {
            errEl.textContent = 'Odkaz musí začínat http:// nebo https:// — jiné adresy nejsou povolené.';
            errEl.style.display = 'block';
            return;
        }

        const payload = {
            song_title: songTitle,
            note: note || null,
            youtube_url: youtube || null
        };

        const { data: { user } } = await supabase.auth.getUser();
        const save = (body) => isEdit
            ? supabase.from('rehearsal_items').update(body).eq('id', item.id)
            : supabase.from('rehearsal_items').insert({ ...body, created_by: user?.id });

        let { error } = await save(payload);

        // Dokud neproběhla migrace 25, sloupec youtube_url v DB není. Uložení
        // bodu na tom ale nesmí padnout — zkusíme to znovu bez odkazu a jen
        // upozorníme, že se odkaz neuložil.
        let ytSkipped = false;
        if (error && migrationHint(error)) {
            const { youtube_url, ...withoutYt } = payload;
            const retry = await save(withoutYt);
            error = retry.error;
            ytSkipped = !error && !!youtube;
        }

        if (error) {
            errEl.textContent = 'Uložení selhalo: ' + error.message + migrationHint(error);
            errEl.style.display = 'block';
            return;
        }
        if (ytSkipped) {
            alert('Bod je uložený, ale odkaz na YouTube se neuložil'
                + migrationHint({ code: '42703' }));
        }
        close();
        onSaved?.();
    });
}
